import { useState, useCallback } from "react";

import { LabelConfigurationUpdate } from "../types";

import { Label } from "@/components/VersionSelector/VersionTab/types";
import { EntityType } from "@/queries/folder-entities/types";
import { useUpdatePromptLabelProtection } from "@/queries/prompts";
import { useUpdateToolLabelProtection } from "@/queries/tool-registry";
import { useUpdateWorkflowLabelProtection } from "@/queries/workflows";

interface UseLabelConfigurationProps {
  entityType: EntityType;
  onSuccess?: () => void;
  onDeleteLabel?: (label: Label) => void;
}

const useLabelConfiguration = ({
  entityType,
  onSuccess,
  onDeleteLabel,
}: UseLabelConfigurationProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);

  const promptMutation = useUpdatePromptLabelProtection();
  const workflowMutation = useUpdateWorkflowLabelProtection();
  const toolMutation = useUpdateToolLabelProtection();

  const openConfigurationDialog = useCallback((label: Label) => {
    setSelectedLabel(label);
    setIsDialogOpen(true);
  }, []);

  const closeConfigurationDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedLabel(null);
  }, []);

  const handleSave = useCallback(
    async (labelId: number, config: LabelConfigurationUpdate) => {
      const params = {
        labelId,
        is_protected: config.is_protected,
        approver_role_id: config.approver_role_id,
      };

      switch (entityType) {
        case EntityType.TOOL:
          await toolMutation.mutateAsync(params);
          break;
        case EntityType.WORKFLOW:
          await workflowMutation.mutateAsync(params);
          break;
        case EntityType.SKILL_COLLECTION:
          return;
        default:
          await promptMutation.mutateAsync(params);
          break;
      }

      onSuccess?.();
    },
    [promptMutation, workflowMutation, toolMutation, onSuccess, entityType],
  );

  const handleDelete = useCallback(
    (label: Label) => {
      onDeleteLabel?.(label);
      setIsDialogOpen(false);
      setSelectedLabel(null);
    },
    [onDeleteLabel],
  );

  return {
    isDialogOpen,
    setIsDialogOpen,
    selectedLabel,
    openConfigurationDialog,
    closeConfigurationDialog,
    handleSave,
    handleDelete,
    isSaving: (() => {
      switch (entityType) {
        case EntityType.TOOL:
          return toolMutation.isPending;
        case EntityType.WORKFLOW:
          return workflowMutation.isPending;
        case EntityType.SKILL_COLLECTION:
          return false;
        default:
          return promptMutation.isPending;
      }
    })(),
  };
};

export default useLabelConfiguration;
